import { useEffect, useRef, useState } from "react";
import { X, Save, Loader2, Eye, Pencil as PencilIcon, Heading2, Heading3, Bold, List, Code, Link2 } from "lucide-react";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import api, { formatApiErrorDetail } from "@/lib/api";
import ImageUploadField from "@/components/ImageUploadField";

const CATEGORY_OPTIONS = ["Prompting Techniques", "Midjourney", "ChatGPT", "AI News", "Other"];

const EMPTY_FORM = {
  title: "",
  slug: "",
  category: "Prompting Techniques",
  cover_image_url: "",
  excerpt: "",
  content: "",
  author: "PromptHub Team",
  status: "draft",
};

function slugify(str) {
  return str
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/[\s_-]+/g, "-")
    .replace(/(^-|-$)+/g, "");
}

const TOOLBAR = [
  { icon: Heading2, label: "H2", before: "\n## ", after: "" },
  { icon: Heading3, label: "H3", before: "\n### ", after: "" },
  { icon: Bold, label: "Bold", before: "**", after: "**" },
  { icon: List, label: "List", before: "\n- ", after: "" },
  { icon: Code, label: "Code block", before: "\n```\n", after: "\n```\n" },
  { icon: Link2, label: "Link", before: "[", after: "](https://)" },
];

export default function BlogPostModal({ post, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [slugTouched, setSlugTouched] = useState(!!post);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);
  const textareaRef = useRef(null);
  const isEdit = !!post;

  useEffect(() => {
    if (post) {
      setForm({
        title: post.title || "",
        slug: post.slug || "",
        category: post.category || "Prompting Techniques",
        cover_image_url: post.cover_image_url || "",
        excerpt: post.excerpt || "",
        content: post.content || "",
        author: post.author || "PromptHub Team",
        status: post.status || "draft",
      });
      setSlugTouched(true);
    } else {
      setForm(EMPTY_FORM);
      setSlugTouched(false);
    }
  }, [post]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const handleTitleChange = (title) => {
    setForm((f) => ({
      ...f,
      title,
      slug: slugTouched ? f.slug : slugify(title),
    }));
  };

  const insertMarkdown = (before, after) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const selected = form.content.slice(start, end);
    const newValue =
      form.content.slice(0, start) + before + selected + after + form.content.slice(end);
    setForm((f) => ({ ...f, content: newValue }));
    requestAnimationFrame(() => {
      ta.focus();
      const cursor = start + before.length + selected.length;
      ta.setSelectionRange(cursor, cursor);
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isEdit) {
        const { data } = await api.put(`/admin/blog/${post.id}`, form);
        toast.success("Article updated", { description: data.title });
        onSaved && onSaved(data);
      } else {
        const { data } = await api.post("/admin/blog", form);
        toast.success("Article created", { description: data.title });
        onSaved && onSaved(data);
      }
      onClose();
    } catch (err) {
      toast.error(isEdit ? "Update failed" : "Create failed", {
        description: formatApiErrorDetail(err.response?.data?.detail) || err.message,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="blog-post-modal"
      className="fixed inset-0 z-50 flex items-start md:items-center justify-center p-4 bg-black/70 backdrop-blur-sm overflow-y-auto"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <form
        onSubmit={submit}
        className="w-full max-w-3xl my-8 rounded-2xl border border-white/10 bg-[#0E0E12] shadow-2xl"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/5">
          <div>
            <div className="font-mono-label text-[#00E5FF]">
              {isEdit ? "Edit article" : "Create new article"}
            </div>
            <div className="font-display text-lg mt-0.5 line-clamp-1">
              {form.title || "Untitled article"}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="blog-modal-close-btn"
            aria-label="Close"
            className="h-9 w-9 rounded-full text-white/60 hover:text-white hover:bg-white/5 flex items-center justify-center"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="font-mono-label text-white/60">Title</label>
              <input
                required
                data-testid="blog-form-title"
                className="mt-2 w-full rounded-lg bg-black/40 border border-white/10 focus:border-[#00E5FF] focus:outline-none px-3 py-2.5"
                value={form.title}
                onChange={(e) => handleTitleChange(e.target.value)}
              />
            </div>
            <div>
              <label className="font-mono-label text-white/60">Slug (URL)</label>
              <input
                required
                data-testid="blog-form-slug"
                className="mt-2 w-full rounded-lg bg-black/40 border border-white/10 focus:border-[#00E5FF] focus:outline-none px-3 py-2.5 font-mono text-sm"
                value={form.slug}
                onChange={(e) => {
                  setSlugTouched(true);
                  setForm({ ...form, slug: e.target.value });
                }}
              />
              <p className="mt-1.5 text-xs text-white/40">/blog/{form.slug || "your-slug"}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="font-mono-label text-white/60">Category / Tag</label>
              <input
                required
                list="blog-category-suggestions"
                data-testid="blog-form-category"
                className="mt-2 w-full rounded-lg bg-black/40 border border-white/10 focus:border-[#00E5FF] focus:outline-none px-3 py-2.5"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
              />
              <datalist id="blog-category-suggestions">
                {CATEGORY_OPTIONS.map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
            </div>
            <div>
              <label className="font-mono-label text-white/60">Author Name</label>
              <input
                data-testid="blog-form-author"
                className="mt-2 w-full rounded-lg bg-black/40 border border-white/10 focus:border-[#00E5FF] focus:outline-none px-3 py-2.5"
                value={form.author}
                onChange={(e) => setForm({ ...form, author: e.target.value })}
              />
            </div>
          </div>

          <div>
            <label className="font-mono-label text-white/60">Featured Image</label>
            <div className="mt-2">
              <ImageUploadField
                value={form.cover_image_url}
                onChange={(url) => setForm({ ...form, cover_image_url: url })}
              />
            </div>
            <input
              type="text"
              placeholder="…or paste an image URL directly"
              data-testid="blog-form-cover-url"
              className="mt-3 w-full rounded-lg bg-black/40 border border-white/10 focus:border-[#00E5FF] focus:outline-none px-3 py-2.5 text-sm"
              value={form.cover_image_url}
              onChange={(e) => setForm({ ...form, cover_image_url: e.target.value })}
            />
          </div>

          <div>
            <label className="font-mono-label text-white/60">
              Short Summary / Meta Description
              <span className="text-white/40 normal-case"> (150–200 characters)</span>
            </label>
            <textarea
              required
              rows={3}
              maxLength={220}
              data-testid="blog-form-excerpt"
              className="mt-2 w-full rounded-lg bg-black/40 border border-white/10 focus:border-[#00E5FF] focus:outline-none px-3 py-2.5 text-sm"
              value={form.excerpt}
              onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
            />
            <p className="mt-1.5 text-xs text-white/40">{form.excerpt.length}/220</p>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <label className="font-mono-label text-white/60">Article Content (Markdown)</label>
              <button
                type="button"
                onClick={() => setPreview((p) => !p)}
                data-testid="blog-form-preview-toggle"
                className="inline-flex items-center gap-1.5 text-xs text-white/60 hover:text-white transition-colors"
              >
                {preview ? <PencilIcon className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                {preview ? "Edit" : "Preview"}
              </button>
            </div>

            {!preview && (
              <div className="mt-2 flex flex-wrap gap-1.5" data-testid="blog-form-toolbar">
                {TOOLBAR.map(({ icon: Icon, label, before, after }) => (
                  <button
                    key={label}
                    type="button"
                    onClick={() => insertMarkdown(before, after)}
                    data-testid={`blog-toolbar-${label.toLowerCase().replace(/\s+/g, "-")}`}
                    title={label}
                    className="inline-flex items-center gap-1 rounded-md border border-white/10 hover:border-white/30 px-2.5 py-1.5 text-xs text-white/70 hover:text-white transition-colors"
                  >
                    <Icon className="h-3 w-3" />
                    {label}
                  </button>
                ))}
              </div>
            )}

            {preview ? (
              <div
                data-testid="blog-form-preview"
                className="mt-2 rounded-lg border border-white/10 bg-black/20 px-4 py-3 min-h-[220px] prose-static"
              >
                <ReactMarkdown>{form.content || "*Nothing to preview yet.*"}</ReactMarkdown>
              </div>
            ) : (
              <textarea
                ref={textareaRef}
                required
                rows={12}
                data-testid="blog-form-content"
                placeholder="## Introduction&#10;&#10;Write your article using Markdown — headers, **bold**, lists, `code`, and [links](https://example.com)."
                className="mt-2 w-full rounded-lg bg-black/40 border border-white/10 focus:border-[#00E5FF] focus:outline-none px-3 py-2.5 text-sm font-mono leading-relaxed"
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
              />
            )}
          </div>

          <div>
            <label className="font-mono-label text-white/60">Status</label>
            <div className="mt-2 inline-flex rounded-full border border-white/10 p-1" data-testid="blog-form-status">
              {["draft", "published"].map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setForm({ ...form, status: s })}
                  data-testid={`blog-form-status-${s}`}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium capitalize transition-colors ${
                    form.status === s
                      ? s === "published"
                        ? "bg-[#00E5FF] text-[#050505]"
                        : "bg-white text-[#050505]"
                      : "text-white/60 hover:text-white"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-white/5 bg-black/20 rounded-b-2xl">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            data-testid="blog-modal-cancel-btn"
            className="px-5 py-2 rounded-full text-sm text-white/70 hover:text-white border border-white/15 hover:border-white/30 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="blog-modal-save-btn"
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold text-white bg-[#E0007A] hover:bg-[#ff1493] glow-magenta transition-transform hover:scale-[1.02] disabled:opacity-50"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {saving ? "Saving…" : isEdit ? "Save changes" : "Create article"}
          </button>
        </div>
      </form>
    </div>
  );
}
